import { createTheme } from '@mui/material';

import { ReactComponent as ArrowIcon } from 'src/assets/svg/chevron-down.svg';
import { ReactComponent as AvatarPlaceholder } from 'src/assets/svg/avatar-placeholder.svg';
import { ReactComponent as WarningIcon } from 'src/assets/svg/warning.svg';
import { ReactComponent as WarningIconHos } from 'src/assets/svg/warning-hos.svg';
import { ReactComponent as SuccessIcon } from 'src/assets/svg/success-in-circle.svg';
import { ReactComponent as SuccessIconHos } from 'src/assets/svg/success-in-hos.svg';
import { ReactComponent as CheckboxCheckedIcon } from 'src/assets/svg/checkbox-checked.svg';
import { ReactComponent as CheckboxUncheckedIcon } from 'src/assets/svg/checkbox-unchecked.svg';

import {
    breakpoints,
    palette,
    hosPalette,
    PRIMARY_FONT_FAMILY,
    SECONDARY_FONT_FAMILY,
    PRIMARY_FONT_FAMILY_HOS,
    SECONDARY_FONT_FAMILY_HOS,
    TERTIARY_FONT_FAMILY_HOS,
} from './constants';

const breakpointTheme = createTheme({ breakpoints });

const TABLET_MEDIA_QUERY = breakpointTheme.breakpoints.up('lg');

export const theme = createTheme({
    palette,

    shape: { borderRadius: 0 },

    breakpoints,

    typography: {
        fontFamily: PRIMARY_FONT_FAMILY,

        h1: {
            fontSize: '44px',
            fontWeight: 700,
            lineHeight: '100%',
            fontFamily: SECONDARY_FONT_FAMILY,
            letterSpacing: 0,
            textTransform: 'uppercase',
            [TABLET_MEDIA_QUERY]: {
                fontSize: '56px',
            },
        },

        h2: {
            fontSize: '28px',
            fontWeight: 700,
            lineHeight: '100%',
            fontFamily: SECONDARY_FONT_FAMILY,
            letterSpacing: 0,
            textTransform: 'uppercase',
        },

        h3: {
            fontSize: '21px',
            fontWeight: 700,
            lineHeight: '100%',
            fontFamily: SECONDARY_FONT_FAMILY,
            letterSpacing: 0,
            textTransform: 'uppercase',
        },

        h4: {
            fontSize: '18px',
            fontWeight: 700,
            lineHeight: '100%',
            fontFamily: SECONDARY_FONT_FAMILY,
            letterSpacing: 0,
            textTransform: 'uppercase',
        },

        h5: {
            fontWeight: 300,
            fontSize: '18px',
            lineHeight: '150%',
            letterSpacing: '0.03em',
        },

        h6: {
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '100%',
            fontFamily: SECONDARY_FONT_FAMILY,
            letterSpacing: '0.03em',
        },

        subtitle1: {
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '140%',
            letterSpacing: '0.03em',
        },

        subtitle2: {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '140%',
            letterSpacing: '0.03em',
        },

        subtitle3: {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '140%',
            letterSpacing: '0.03em',
            [TABLET_MEDIA_QUERY]: {
                fontSize: '16px',
            },
        },

        body1: {
            fontSize: '16px',
            fontWeight: 300,
            lineHeight: '140%',
            letterSpacing: '0.03em',
        },

        body2: {
            fontSize: '14px',
            fontWeight: 300,
            lineHeight: '140%',
            letterSpacing: '0.03em',
        },

        body3: {
            fontSize: '14px',
            fontWeight: 300,
            lineHeight: '140%',
            letterSpacing: '0.03em',
            [TABLET_MEDIA_QUERY]: {
                fontSize: '16px',
                lineHeight: '150%',
            },
        },

        body4: {
            color: palette.common.black,
            fontFamily: PRIMARY_FONT_FAMILY,
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: '300',
            lineHeight: '140%',
            letterSpacing: '0.36px',
        },

        subtitle4: {
            color: palette.text.primary,
            fontFamily: SECONDARY_FONT_FAMILY,
            fontSize: '15px',
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: '140%',
            textTransform: 'uppercase',
        },

        overlineMedium: {
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '125%',
            letterSpacing: '0.025em',
            textTransform: 'none',
        },

        overlineRegular: {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '125%',
            letterSpacing: '0.025em',
            textTransform: 'none',
            [TABLET_MEDIA_QUERY]: {
                fontWeight: 400,
            },
        },

        button: {
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '100%',
            fontFamily: SECONDARY_FONT_FAMILY,
            letterSpacing: '0.03em',
            textTransform: 'none',
        },
    },

    components: {
        MuiUseMediaQuery: {
            defaultProps: {
                noSsr: true,
            },
        },

        MuiAlert: {
            defaultProps: {
                iconMapping: {
                    success: <SuccessIcon width="24px" height="24px" />,
                    error: <WarningIcon width="24px" height="24px" />,
                    info: <WarningIcon width="24px" height="24px" />,
                    warning: <WarningIcon width="24px" height="24px" />,
                },
            },

            styleOverrides: {
                standardError: {
                    color: palette.error.main,
                    backgroundColor: palette.error.light,
                    padding: '8px 16px',
                    borderRadius: '0',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    minHeight: '52px',
                },

                standardSuccess: {
                    gap: '15px',
                },

                standardWarning: {
                    backgroundColor: palette.warning.light,
                    color: palette.text.primary,
                },

                filled: {
                    padding: '10px',
                    borderRadius: '0',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    fontWeight: '500',
                },

                filledInfo: {
                    color: palette.primary.contrastText,
                    backgroundColor: palette.primary.main,
                },

                filledWarning: {
                    color: palette.text.primary,
                    backgroundColor: palette.warning.dark,
                },

                filledSuccess: {
                    color: palette.success.contrastText,
                    backgroundColor: palette.success.dark,
                },

                icon: {
                    padding: 0,
                    marginRight: 0,
                    width: '24px',
                    height: '24px',
                    color: 'inherit',
                },

                message: {
                    padding: 0,
                },
            },
        },

        MuiInputLabel: {
            styleOverrides: {
                asterisk: {
                    color: palette.primary.main,
                },
            },
        },

        MuiSelect: {
            defaultProps: {
                IconComponent: ArrowIcon,
                disableUnderline: true,
            },
            styleOverrides: {
                icon: {
                    width: '18px',
                    height: '18px',
                    color: palette.common.black,
                },
                select: {
                    padding: '10px 0',
                    background: 'none',
                    textWrap: 'wrap',
                },
            },
        },

        MuiFormLabel: {
            styleOverrides: {
                root: {
                    padding: '6px 0',
                    '&[data-shrink=true]': {
                        transform: 'translateY(-4px)',
                        fontSize: '14px',
                        padding: '0',
                    },
                    '&[aria-label="placeholder label"]': {
                        color: palette.text.placeholder,
                        '&[data-shrink="true"]': {
                            display: 'none',
                        },
                    },
                },
            },
        },

        MuiAutocomplete: {
            defaultProps: {
                popupIcon: (
                    <ArrowIcon
                        width={18}
                        height={18}
                        css={{
                            color: palette.common.black,
                        }}
                    />
                ),
            },
            styleOverrides: {
                input: {
                    padding: '10px 0 !important',
                },
                paper: {
                    margin: '10px 0',
                    borderRadius: 0,
                    background: palette.background.default,
                    boxShadow: '2px 4px 30px rgba(0, 0, 0, 0.2)',
                },
                listbox: {
                    padding: '8px',
                },
                option: {
                    color: palette.text.primary,
                    padding: '11px',
                    '&:hover': {
                        background: palette.background.paper,
                    },
                    '&[aria-selected="true"]': {
                        background: `${palette.common.black} !important`,
                        color: palette.primary.contrastText,
                    },
                },
            },
        },

        MuiMenu: {
            styleOverrides: {
                paper: {
                    margin: '8px 0 !important',
                    borderRadius: '0 !important',
                    background: `${palette.background.default} !important`,
                    boxShadow: '2px 4px 40px 0 #00000014',
                },
            },
        },

        MuiInputBase: {
            styleOverrides: {
                root: {
                    '&.Mui-focused:not(.Mui-disabled)': {
                        borderBottom: `1px solid ${palette.common.black}`,
                    },
                    borderBottom: `1px solid ${palette.divider}`,
                },
                input: {
                    '::placeholder': {
                        color: palette.text.placeholder,
                        opacity: 1,
                    },

                    '&.Mui-disabled::placeholder': {
                        color: palette.text.disabled,
                    },
                },
            },
        },

        MuiInput: {
            defaultProps: {
                disableUnderline: true,
            },
            styleOverrides: {
                input: {
                    paddingBlock: '10px !important',
                },
                root: {
                    borderBottom: `1px solid ${palette.divider}`,

                    '&:hover:not(.Mui-disabled):before': {
                        borderBottom: `1px solid ${palette.common.black}`,
                    },

                    '&.Mui-focused:not(.Mui-disabled)': {
                        borderBottom: `1px solid ${palette.common.black}`,
                    },

                    '&.Mui-error': {
                        borderBottom: `1px solid ${palette.error.main}`,
                    },
                },
            },
        },

        MuiList: {
            styleOverrides: {
                root: {
                    padding: '8px',
                },
            },
        },

        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: palette.text.primary,
                    padding: '10px',
                    '&:hover': {
                        background: palette.background.paper,
                    },
                    '&[aria-selected="true"]': {
                        background: `${palette.common.black} !important`,
                        color: palette.primary.contrastText,
                    },
                    textWrap: 'wrap',
                },
            },
        },

        MuiBadge: {
            styleOverrides: {
                badge: {
                    padding: '3px',
                    height: '16px',
                    minWidth: '16px',
                    fontSize: '9px',
                    fontWeight: 700,
                },
            },
        },

        MuiAvatar: {
            styleOverrides: {
                root: {
                    background: palette.grey[100],
                },
            },

            defaultProps: {
                children: (
                    <AvatarPlaceholder width="32px" height="32px" aria-label="avatar placeholder" />
                ),
            },
        },

        MuiChip: {
            styleOverrides: {
                root: {
                    backgroundColor: palette.background.paper,
                },

                label: {
                    padding: 0,
                },
            },
        },

        MuiSlider: {
            styleOverrides: {
                mark: {
                    width: '1px',
                    height: '8px',
                    background: palette.divider,
                    '&.MuiSlider-markActive': {
                        background: palette.text.primary,
                    },
                },
                markLabel: {
                    fontSize: '14px',
                    color: palette.common.black,
                },
                rail: {
                    background: palette.divider,
                    height: '2px',
                    opacity: 1,
                },
                track: {
                    color: palette.text.primary,
                    border: 'none',
                    height: '2px',
                },
                thumb: {
                    width: '12px',
                    height: '12px',
                    background: palette.text.primary,
                    boxShadow: 'none !important',
                    '&:before': {
                        boxShadow: 'none',
                    },
                },
            },
        },

        MuiCheckbox: {
            styleOverrides: {
                root: {
                    padding: '2px !important',
                    color: palette.common.black,
                    '&:hover': {
                        color: palette.primary.main,
                    },
                },
            },
            defaultProps: {
                icon: <CheckboxUncheckedIcon />,
                checkedIcon: <CheckboxCheckedIcon color={palette.primary.main} />,
            },
        },

        MuiButton: {
            styleOverrides: {
                root: {
                    minWidth: 'unset',
                    maxWidth: 'unset',
                    boxShadow: 'none',
                    ':hover': {
                        boxShadow: 'none',
                        background: palette.common.black,
                    },
                    ':disabled': {
                        color: palette.common.white,
                        background: palette.button.disabled,
                    },
                },
            },

            defaultProps: {
                disableRipple: true,
            },
        },

        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    body3: 'p',
                    subtitle3: 'h6',
                },
            },
        },

        MuiSkeleton: {
            styleOverrides: {
                rounded: {
                    borderRadius: '100px',
                },
            },
        },
        MuiStep: {
            styleOverrides: {
                root: {
                    paddingLeft: '0',
                    paddingRight: '0',
                },
            },
        },
    },

    custom: {
        agreementContainer: {
            height: 350,
            width: 700,
        },
    },
});

export const hosTheme = createTheme(theme, {
    palette: hosPalette,

    typography: {
        h1: {
            fontWeight: 400,
            fontFamily: PRIMARY_FONT_FAMILY_HOS,
            textTransform: 'none',
        },

        h2: {
            fontSize: '26px',
            fontFamily: SECONDARY_FONT_FAMILY_HOS,
            textTransform: 'none',
        },

        h3: {
            fontWeight: 400,
            fontFamily: SECONDARY_FONT_FAMILY_HOS,
            textTransform: 'none',
        },

        h4: {
            fontFamily: SECONDARY_FONT_FAMILY_HOS,
            textTransform: 'none',
        },

        h6: {
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '100%',
            fontFamily: SECONDARY_FONT_FAMILY_HOS,
        },

        body1: {
            fontFamily: SECONDARY_FONT_FAMILY_HOS,
        },

        body2: {
            fontFamily: SECONDARY_FONT_FAMILY_HOS,
        },

        body4: {
            fontFamily: SECONDARY_FONT_FAMILY_HOS,
        },

        body3: {
            fontSize: '16px',
            fontFamily: SECONDARY_FONT_FAMILY_HOS,
            fontWeight: 400,
        },

        subtitle1: {
            fontSize: '16px',
            fontFamily: SECONDARY_FONT_FAMILY_HOS,
        },

        subtitle2: {
            fontSize: '13px',
            fontFamily: TERTIARY_FONT_FAMILY_HOS,
        },

        subtitle3: {
            fontFamily: SECONDARY_FONT_FAMILY_HOS,
        },

        subtitle4: {
            fontWeight: 400,
            fontFamily: SECONDARY_FONT_FAMILY_HOS,
            textTransform: 'none',
        },

        button: {
            fontWeight: 400,
            fontFamily: SECONDARY_FONT_FAMILY_HOS,
            textTransform: 'none',
        },
    },

    components: {
        MuiAlert: {
            defaultProps: {
                iconMapping: {
                    success: <SuccessIconHos width="34px" height="34px" />,
                    error: <WarningIconHos width="34px" height="34px" />,
                    info: <WarningIconHos width="34px" height="34px" />,
                    warning: <WarningIconHos width="34px" height="34px" />,
                },
            },
            styleOverrides: {
                icon: {
                    width: '34px',
                    height: '34px',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        background: `${hosPalette.background.paper} !important`,
                        color: `${hosPalette.secondary.light} !important`,
                    },
                    textWrap: 'wrap',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    ':disabled': {
                        color: palette.common.white,
                        background: hosPalette.button.disabled,
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    '::placeholder': {
                        fontFamily: TERTIARY_FONT_FAMILY_HOS,
                    },

                    '&.Mui-disabled::placeholder': {
                        fontFamily: TERTIARY_FONT_FAMILY_HOS,
                    },
                },
            },
        },
        MuiInput: {
            styleOverrides: {
                root: {
                    borderBottom: `0.5px solid ${hosPalette.divider}`,
                },
            },
        },

        MuiInputLabel: {
            styleOverrides: {
                asterisk: {
                    color: hosPalette.primary.main,
                },
            },
        },

        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    gap: '10px',
                    alignItems: 'flex-start',
                },
            },
        },

        MuiCheckbox: {
            styleOverrides: {
                root: {
                    padding: '2px !important',
                    color: hosPalette.common.black,
                    '&:hover': {
                        color: hosPalette.primary.main,
                    },
                },
            },
            defaultProps: {
                icon: <CheckboxUncheckedIcon />,
                checkedIcon: <CheckboxCheckedIcon color={hosPalette.secondary.dark} />,
            },
        },
    },

    custom: {
        borderRadius: {
            10: '10px',
            16: '16px',
            23: '23px',
            100: '100px',
        },

        agreementContainer: {
            height: 350,
            width: 700,
        },
    },
});
